import React, { ComponentType, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import moment from 'moment';
import fileDownload from 'js-file-download';
import { RootState } from 'store/types';
import { useHistory } from 'react-router-dom';
import { UPDATE_PREREG_STATUS } from 'services/orchestration/mutations';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { Icon } from 'components/Icon';
import Typography from 'components/UI/Typography';
import Divider from 'components/UI/Divider';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import Footer, { SmallFooter } from 'components/UI/Footer/Footer';
import { Svg } from 'components/UI/Svg';
import { Color } from 'modules/styles/colors';
import { IconSize, FontSize, FontWeight } from 'modules/styles/variables';
import { MuiBox } from 'theme/material-ui';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { FlatList } from 'react-native';
import dayjs from 'dayjs';
import * as actions from 'store/preRegistration/actions';
import {
  CheckListItem,
  SkipPaymentFlagTypes,
  PreRegistration,
  VisitDetail
} from 'store/preRegistration/types';
import { convertToLowerKabobCase } from 'modules/utils/StringUtils';
import { StepTypeToPathMap, SkipPaymentFlags, StepTypes } from 'lib/constants/preRegister';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { GetFile } from 'services/orchestration/GetFile';
import { Alert } from 'components/Alert';
import { HeadingTypography, StyledCheckCircleIcon, FloatingButton } from '../styled';
import {
  PreRegistrationEventIdSelector,
  PreRegistrationDataSelector,
  PreRegistrationSkipPaymentFlagsSelector,
  PreRegistrationColletionAttempt,
  PreRegistrationShouldSkipPaymentSelector,
  PreRegistrationShouldSkipActivityCompleteSelector,
  PreRegistrationInsuranceChangeSelector,
  PreRegistrationGuarantorChangeSelector
} from 'store/preRegistration/selectors';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import { ApplyPaymentDataSelector } from 'store/preRegistration/payment/selectors';
import { ApplyPaymentResponse } from 'store/preRegistration/payment/types';
import { headerWithJWT } from '../utils';
import { HeadingMuiBox, PreRegConfirmModal } from '../common';
import FlexBox from 'components/UI/Layout/FlexBox';

export interface Props {
  preRegistrationData: PreRegistration;
  currentUrl?: string;
  referringUrl?: string;
  receiptData: null | ApplyPaymentResponse;
  skipPaymentFlags: SkipPaymentFlagTypes;
  collectionAttemptStatus: boolean;
  shouldSkipPaymentFlag: boolean;
  PreRegistrationEventId: string;
  visitDetail: VisitDetail;
  shouldSkipActivity: boolean;
  isGuarantorChanged: boolean;
  isInsuranceChanged: boolean;
}

export const PreRegisterationChecklist = ({
  preRegistrationData,
  currentUrl,
  referringUrl,
  receiptData,
  skipPaymentFlags,
  collectionAttemptStatus,
  shouldSkipPaymentFlag,
  PreRegistrationEventId,
  shouldSkipActivity,
  isInsuranceChanged,
  isGuarantorChanged
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { visitDetail } = preRegistrationData;
  const [hasRequestedExit, setRequestExit] = useState(false);
  const {
    startDate,
    location,
    activity,
    facilityCode,
    payment,
    id,
    serviceDescription
  } = visitDetail;
  const [updatePreregStatus, { loading: updateLoading }] = useMutation(UPDATE_PREREG_STATUS);

  const [getFile] = useLazyQuery(GetFile, {
    context: {
      clientName: 'orchestration',
      headers: headerWithJWT()
    },
    onCompleted: fileData => {
      if (!fileData || !fileData.getFile) {
        Alert.alert('Failed to download the file. Please try again later.');
      } else {
        const { docCacheId, data } = fileData.getFile;
        fileDownload(Buffer.from(data, 'base64'), docCacheId);
      }
    },
    onError: () => Alert.alert('Failed to download the file. Please try again later.')
  });

  const checkListData = preRegistrationData?.checkList || [];
  const baseUrl = '/pre-reg';
  const insuranceStep = checkListData.find((item: CheckListItem) => item.type === 'Insurance');

  const telephoneFCC = !!activity?.FCCContact;

  const facilityName = location?.name || '';

  const currentDay = dayjs();

  const cutOffTime = dayjs(startDate)
    .subtract(1, 'd')
    .hour(21)
    .minute(30);

  const isBeforeCutOffDate = currentDay < cutOffTime;

  const requestExit = () => {
    setRequestExit(true);
  };
  const stayHere = () => {
    setRequestExit(false);
  };

  const onClose = () => {
    setRequestExit(false);
    dispatch({ type: actions.PRE_REGISTRATION_RESET });
    window.sessionStorage.setItem('PRE_REG', '');
    history.push('/pre-reg/logged-out');
  };

  const sendAnalyticsEvent = () => {
    const { patientResponsibilityInCents, priorBalanceInCents } = payment || {};
    const priorBalanceExists = priorBalanceInCents > 0 ? 'Yes' : 'No';
    const estimateAvailable = patientResponsibilityInCents > 0 ? 'Yes' : 'No';
    let insuranceStatus = 'Has Insurance';
    if (skipPaymentFlags[SkipPaymentFlags.SELF_PAY_ELECT]) {
      insuranceStatus = 'Self Pay Elect';
    } else if (skipPaymentFlags[SkipPaymentFlags.SELF_PAY]) {
      insuranceStatus = 'Self Pay';
    }

    const additionalInfo = `Prior Balance Exists (${priorBalanceExists}). Estimate Available (${estimateAvailable}). Insurance Status (${insuranceStatus})`;
    const amplitudeEventData: AmplitudeEventData = {
      currentUrl,
      referringUrl,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      form_location: facilityName,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      form_detail_additional_info: additionalInfo
    };
    analyticsService.logEvent(AnalyticsEvent.HospitalFormPaymentStart, amplitudeEventData);
  };

  const goToPayment = () => {
    sendAnalyticsEvent();
    history.push(`/pre-reg/payment`);
  };

  const goToComplete = async () => {
    const mutationPayload: Record<string, any> = {
      facilityCode,
      finNumber: id,
      event: {
        id: PreRegistrationEventId,
        complete: true
      }
    };

    if (!shouldSkipActivity) {
      const activity = {
        Mrn: preRegistrationData.patientInfo?.id,
        Event: {
          EventType: 'Previsit',
          EventStatus: 'Completed',
          EventDate: new Date().toISOString(),
          FollowUpDate: startDate || ''
        }
      };
      mutationPayload.activity = activity;
      if (isInsuranceChanged && isGuarantorChanged) {
        activity.Event.EventStatus = 'PreVisitPartCompleteNeedGuarantorUpdate';
        await updatePreregStatus({
          variables: {
            updatePreregStatusInput: mutationPayload
          },
          context: {
            clientName: 'orchestration',
            header: headerWithJWT()
          }
        });
        activity.Event.EventStatus = 'PreVisitPartCompleteNeedInsuranceUpdate';
      } else if (isInsuranceChanged) {
        activity.Event.EventStatus = 'PreVisitPartCompleteNeedInsuranceUpdate';
      } else if (isGuarantorChanged) {
        activity.Event.EventStatus = 'PreVisitPartCompleteNeedGuarantorUpdate';
      }
    }

    const res = await updatePreregStatus({
      variables: {
        updatePreregStatusInput: mutationPayload
      },
      context: {
        clientName: 'orchestration',
        header: headerWithJWT()
      }
    });

    if (res.data.updatePreregStatus.success) {
      dispatch({
        type: actions.UPDATE_PROCESS_STATUS,
        payload: res.data.updatePreregStatus.success
      });

      const amplitudeEventData: AmplitudeEventData = {
        currentUrl,
        referringUrl,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_location: facilityName
      };
      analyticsService.logEvent(AnalyticsEvent.HospitalFormComplete, amplitudeEventData);

      history.push(`/pre-reg/complete`);
    } else {
      Alert.alert('Failure', 'Checklist failed to complete');
    }
  };

  const handleRegistrationDetail = (item: CheckListItem, isDisabled?: boolean) => {
    if (isDisabled) {
      Alert.alert('You need to complete Verify Insurance first.');
      return;
    }

    if (preRegistrationData.complete) {
      return;
    }

    const isSignForm =
      item.type === StepTypes.CONSENT_FORM && item.name !== 'Self-PayAcknowledgementLetter';

    if (item.complete) {
      if (!isSignForm || (isSignForm && item.docCacheId)) {
        return;
      }
    }

    if (item.type) {
      const amplitudeEventData: AmplitudeEventData = {
        currentUrl,
        referringUrl,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_location: facilityName,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_detail_type: `Hospital Registration: ${item.title}`
      };
      analyticsService.logEvent(AnalyticsEvent.HospitalFormDetailStart, amplitudeEventData);
      history.push(`${baseUrl}${StepTypeToPathMap.get(item.type)}`, { id: item.id });
    }
  };

  const handleGoToNext = () => {
    const item = checkListData.find((item: CheckListItem) => !item.complete && item.required);
    if (!item) {
      return;
    }
    const url = `${baseUrl}${StepTypeToPathMap.get(item.type)}`;
    history.push(url, { id: item.id });
  };

  const downloadReport = (item: CheckListItem, disabled: boolean) => {
    // handling arrow button press when download is disabled
    if (!item.complete || !item.docCacheId) {
      handleRegistrationDetail(item, disabled);
    } else {
      getFile({ variables: { docCacheId: item.docCacheId } });
    }
  };

  const getHeaderMessage = () => {
    const titleLocationString = facilityName ? ` at ${facilityName}` : '';
    const serviceName = serviceDescription ? ` ${serviceDescription}` : '';
    const dateString = startDate ? ` on ${moment(startDate).format('MM/DD/YYYY')}` : '';
    return `Please complete the following prior to your${serviceName} visit${titleLocationString}${dateString}.`;
  };

  const isComplete = !checkListData.filter((item: CheckListItem) => !item.complete && item.required)
    .length;

  const renderItem = (props: { item: CheckListItem }) => {
    const { item } = props;
    if (!item.required) {
      return null;
    }
    let disabled = false;
    if (
      (item.name === 'Mspq' || item.name === 'Self-PayAcknowledgementLetter') &&
      insuranceStep &&
      !insuranceStep.complete
    ) {
      disabled = true;
    }

    const showAction =
      (!preRegistrationData.complete &&
        (!item.complete || item.type === 'Mspq' || item.type === 'ConsentForm')) ||
      (preRegistrationData.complete &&
        item.docCacheId &&
        (item.type === 'Mspq' || item.type === 'ConsentForm'));

    return (
      <>
        <ListItem
          onClick={() => handleRegistrationDetail(item, disabled)}
          data-testid={convertToLowerKabobCase(item.title)}
          disabled={disabled}
        >
          <ListItemAvatar>
            <StyledCheckCircleIcon
              fontSize="large"
              isComplete={item.complete}
              data-testid={
                item.complete
                  ? convertToLowerKabobCase(item.title, '-checklist-icon-enabled')
                  : convertToLowerKabobCase(item.title, '-checklist-icon-disabled')
              }
            />
          </ListItemAvatar>
          <ListItemText
            primary={item.title}
            data-testid={convertToLowerKabobCase(item.title, '-label')}
          />
          {showAction ? (
            <ListItemSecondaryAction onClick={() => downloadReport(item, disabled)}>
              <Svg
                name={item.complete && item.docCacheId ? 'get-app' : 'keyboard-arrow-right'}
                set="material"
                fill={Color.black}
                data-testid={
                  item.complete && item.docCacheId
                    ? convertToLowerKabobCase(item.title, '-get-app-icon')
                    : convertToLowerKabobCase(item.title, '-arrow-right-icon')
                }
              />
            </ListItemSecondaryAction>
          ) : null}
        </ListItem>
        <Divider />
      </>
    );
  };

  const renderPaymentCheckList = () => {
    const title = 'Payment Options';
    return (
      <>
        <ListItem data-testid={convertToLowerKabobCase(title)}>
          <ListItemAvatar>
            <StyledCheckCircleIcon
              fontSize="large"
              isComplete
              data-testid={convertToLowerKabobCase(title, '-checklist-icon-enabled')}
            />
          </ListItemAvatar>
          <ListItemText primary={title} data-testid={convertToLowerKabobCase(title, '-label')} />
        </ListItem>
        <Divider />
      </>
    );
  };

  if (updateLoading) {
    return <SpinnerModal isLoading={updateLoading} />;
  }

  const hasSecondaryInsurance = visitDetail?.insurance?.length > 1;

  const shouldShowPayScreenNext =
    !shouldSkipPaymentFlag &&
    !telephoneFCC &&
    isBeforeCutOffDate &&
    !hasSecondaryInsurance &&
    (payment?.priorBalanceInCents || payment?.patientResponsibilityInCents);

  return (
    <>
      <MuiBox flexDirection="column" alignContent="center">
        <HeadingMuiBox>
          <HeadingTypography fontSize={FontSize.heading} data-testid="pre-visit-checklist-header">
            Pre-Visit Checklist
          </HeadingTypography>
          <Icon name="close" onClick={requestExit} size={IconSize.large} data-testid="close-icon" />
          <PreRegConfirmModal
            icon="ExclamationCircle"
            position="middle"
            isOpen={hasRequestedExit}
            title="Are you sure you want to logout?"
            onConfirm={onClose}
            onCancel={stayHere}
            confirmLabel="Logout"
            cancelLabel="Keep going"
            description="Any unsaved changes will be lost."
            backdropColor="#213245"
          />
        </HeadingMuiBox>

        <MuiBox m={1} p={1}>
          <Typography
            fontSize={FontSize.large}
            fontWeight={FontWeight.bold}
            data-testid="pre-visit-checklist-form-title"
          >
            {getHeaderMessage()}
          </Typography>
        </MuiBox>
        <List>
          <FlatList
            data={checkListData}
            keyExtractor={(item: CheckListItem, index: number) => `${item.name}+${index}`}
            renderItem={renderItem}
          />
          {receiptData?.receiptNumber || collectionAttemptStatus ? renderPaymentCheckList() : null}
        </List>
        <Spacer size="medium" />
        {!preRegistrationData?.complete ? (
          <FloatingButton>
            {isComplete && shouldShowPayScreenNext ? (
              <StyledButton
                data-testid="pre-reg-continue-button"
                title="next"
                onPress={goToPayment}
              >
                Continue to payment options
              </StyledButton>
            ) : null}
            {isComplete && !shouldShowPayScreenNext ? (
              <StyledButton data-testid="pre-reg-finish-button" title="next" onPress={goToComplete}>
                Finish
              </StyledButton>
            ) : null}

            {!isComplete ? (
              <StyledButton
                data-testid="pre-reg-next-button"
                title="next"
                onPress={handleGoToNext}
                disabled={isComplete}
              >
                Next
              </StyledButton>
            ) : null}
            <Spacer size="small" />
          </FloatingButton>
        ) : null}
        {preRegistrationData?.complete &&
        (receiptData?.receiptNumber || collectionAttemptStatus) ? (
          <FloatingButton>
            <StyledButton data-testid="pre-reg-finish-button" title="next" onPress={goToComplete}>
              Complete
            </StyledButton>
          </FloatingButton>
        ) : null}
        <div>
          <Spacer size="medium" />
          <FlexBox>
            <SmallFooter>
              <Footer />
            </SmallFooter>
          </FlexBox>
        </div>
      </MuiBox>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  PreRegistrationEventId: PreRegistrationEventIdSelector(state),
  preRegistrationData: PreRegistrationDataSelector(state),
  currentUrl: currentLocationPathNameSelector(state),
  referringUrl: previousLocationPathNameSelector(state),
  receiptData: ApplyPaymentDataSelector(state),
  skipPaymentFlags: PreRegistrationSkipPaymentFlagsSelector(state),
  collectionAttemptStatus: PreRegistrationColletionAttempt(state),
  shouldSkipPaymentFlag: PreRegistrationShouldSkipPaymentSelector(state),
  shouldSkipActivity: PreRegistrationShouldSkipActivityCompleteSelector(state),
  isInsuranceChanged: PreRegistrationInsuranceChangeSelector(state),
  isGuarantorChanged: PreRegistrationGuarantorChangeSelector(state)
});

export default connect(mapStateToProps)(PreRegisterationChecklist as ComponentType);
